import { type FC, useEffect, useEffectEvent } from 'react';

import dayjs from 'dayjs';
import customParseFormat from 'dayjs/plugin/customParseFormat';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import utc from 'dayjs/plugin/utc';

import { TimeUnitPicker } from '@shared/components/ui/custom/generic-date-range-picker/TimeUnitPicker';

dayjs.extend(utc);
dayjs.extend(customParseFormat);
dayjs.extend(isSameOrBefore);

export interface TimePickerRowProps {
  dateLabel: string;
  hourValue: string;
  minuteValue: string;
  onHourChange: (v: string) => void;
  onMinuteChange: (v: string) => void;
}

export const TimePickerRow: FC<TimePickerRowProps> = ({
  dateLabel,
  hourValue,
  minuteValue,
  onHourChange,
  onMinuteChange,
}) => {
  const hours = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'));
  const minutes = Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0'));

  const isTodayUtc = dayjs.utc(dateLabel, 'MMM D YYYY').isSame(dayjs.utc(), 'day');
  const nowUtc = dayjs.utc();

  const isHourDisabled = (h: string) => {
    if (!isTodayUtc) return false;
    return Number.parseInt(h) > nowUtc.hour();
  };

  const isMinuteDisabled = (m: string) => {
    if (!isTodayUtc) return false;

    if (Number.parseInt(hourValue) < nowUtc.hour()) return false;

    if (Number.parseInt(hourValue) === nowUtc.hour()) {
      return Number.parseInt(m) > nowUtc.minute();
    }

    return true;
  };

  const clampToNow = useEffectEvent(() => {
    if (!isTodayUtc) return;

    const currentHour = Number.parseInt(hourValue);
    const currentMinute = Number.parseInt(minuteValue);
    const maxHour = nowUtc.hour();
    const maxMinute = nowUtc.minute();

    const targetHour = maxHour.toString().padStart(2, '0');
    const targetMinute = maxMinute.toString().padStart(2, '0');

    if (currentHour > maxHour) {
      if (hourValue !== targetHour) onHourChange(targetHour);
      if (minuteValue !== targetMinute) onMinuteChange(targetMinute);
    } else if (currentHour === maxHour && currentMinute > maxMinute) {
      if (minuteValue !== targetMinute) onMinuteChange(targetMinute);
    }
  });

  useEffect(() => {
    clampToNow();
  }, [isTodayUtc, dateLabel]);

  return (
    <div className="flex items-center gap-3" data-testid="time-picker-row">
      <span className="text-sm">{dateLabel}</span>

      <div className="flex items-center gap-2">
        <TimeUnitPicker value={hourValue} onChange={onHourChange} options={hours} isOptionDisabled={isHourDisabled} />

        <span>:</span>

        <TimeUnitPicker
          value={minuteValue}
          onChange={onMinuteChange}
          options={minutes}
          isOptionDisabled={isMinuteDisabled}
        />

        <span className="text-xs">UTC</span>
      </div>
    </div>
  );
};
